// src/pages/ArtistReports.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import PermissionGuard from "../components/PermissionGuard";
import {
  FiEye,
  FiMessageSquare,
  FiCheck,
  FiAlertTriangle,
  FiClock,
  FiUser,
  FiSearch,
  FiFilter,
} from "react-icons/fi";

/* Colors */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b45309",
  hold: "#7c3aed",
};

const STATUS_RANK = { open: 0, hold: 1, resolved: 2 };

export default function ArtistReports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState({
    open: false,
    id: "",
    artist: "",
    reportedBy: "",
    reason: "",
    timestamp: null,
    adminStatus: "open",
    adminComment: "",
  });
  const [toast, setToast] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  // UI controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | open | hold | resolved

  // compute edit permission (view guard is handled by PermissionGuard)
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("employee"));
    const role = user?.role;
    const perms = user?.permissions || [];
    setCanEdit(role === "super_admin" || perms.includes("view_artist_reports"));
  }, []);

  // live read (keep raw list; sort/filter in memo to avoid hooks warnings)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "artist_reports"), (snap) => {
      const list = snap.docs.map((d) => {
        const x = d.data() || {};
        // normalize field names (support old keys too)
        const artist = x.reportedArtist || x.artist_name || "—";
        const reportedBy = x.reportingUser || x.reported_by || "—";
        const ts = x.timestamp || x.date || null;
        const adminStatus = String(x.adminStatus || "open").toLowerCase();
        const adminComment = x.adminComment || "";
        return {
          id: d.id,
          artist,
          reportedBy,
          reason: x.reason || "-",
          timestamp: ts,
          adminStatus,
          adminComment,
          raw: x,
        };
      });
      setRows(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filteredSorted = useMemo(() => {
    let list = [...rows];

    // search across common fields
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const hay = [
          r.id,
          r.artist,
          r.reportedBy,
          r.reason,
          formatDateTime(r.timestamp),
          r.adminStatus,
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    if (statusFilter !== "All") {
      list = list.filter((r) => r.adminStatus === statusFilter);
    }

    // open → hold → resolved, newest within status
    return list.sort((a, b) => {
      const ar = STATUS_RANK[a.adminStatus] ?? 99;
      const br = STATUS_RANK[b.adminStatus] ?? 99;
      if (ar !== br) return ar - br;
      return (toMillis(b.timestamp) || 0) - (toMillis(a.timestamp) || 0);
    });
  }, [rows, search, statusFilter]);

  const totalOpen = useMemo(
    () => rows.filter((r) => r.adminStatus === "open").length,
    [rows]
  );
  const totalHold = useMemo(
    () => rows.filter((r) => r.adminStatus === "hold").length,
    [rows]
  );
  const totalResolved = useMemo(
    () => rows.filter((r) => r.adminStatus === "resolved").length,
    [rows]
  );

  const openModal = (r) => {
    setViewer({
      open: true,
      id: r.id,
      artist: r.artist,
      reportedBy: r.reportedBy,
      reason: r.reason,
      timestamp: r.timestamp,
      adminStatus: r.adminStatus || "open",
      adminComment: r.adminComment || "",
    });
  };
  const closeModal = () => setViewer((v) => ({ ...v, open: false }));

  async function saveUpdate() {
    if (!viewer.id) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, "artist_reports", viewer.id), {
        adminStatus: String(viewer.adminStatus || "open").toLowerCase(),
        adminComment: viewer.adminComment || "",
        adminUpdatedAt: serverTimestamp(),
      });
      showToast("Report updated");
    } catch (e) {
      console.error(e);
      showToast("Update failed");
    } finally {
      setSaving(false);
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 1600);
  }

  return (
    <PermissionGuard requiredPermission="view_artist_reports">
      <div className="aa-pro-page" style={{ background: COLORS.page, minHeight: "100vh", fontFamily: "Inter, system-ui, sans-serif" }}>
        <div className="aa-pro-hero" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 className="aa-pro-title">Artist Reports</h2>
            <p className="aa-pro-subtitle" style={{ marginTop: 6 }}>Track reported artists, add internal comments, and update status.</p>
          </div>
          <div className="aa-pro-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, minWidth: 240 }}>
            <Badge color={COLORS.warn} icon={<FiAlertTriangle />}>Open: {totalOpen}</Badge>
            <Badge color={COLORS.hold} icon={<FiClock />}>On Hold: {totalHold}</Badge>
            <Badge color={COLORS.ok} icon={<FiCheck />}>Resolved: {totalResolved}</Badge>
          </div>
        </div>

        <div className="aa-pro-toolbar" style={controlsRow}>
          <div style={searchBox}>
            <FiSearch style={{ opacity: 0.7 }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search artist, reporter, reason, date, status, id…"
              style={searchInput}
            />
          </div>
          <div style={controlGroup}>
            <FiFilter />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={select}>
              <option>All</option>
              <option value="open">Open</option>
              <option value="hold">Hold</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        <div className="aa-pro-table-shell" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr>
                <Th sticky>Artist</Th>
                <Th sticky>Reported By</Th>
                <Th sticky>Reason</Th>
                <Th sticky>Date</Th>
                <Th sticky>Status</Th>
                <Th sticky>Action</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={tdMuted}>Loading…</td>
                </tr>
              ) : filteredSorted.length === 0 ? (
                <tr>
                  <td colSpan={6} style={tdMuted}>No reports found.</td>
                </tr>
              ) : (
                filteredSorted.map((r, idx) => {
                  const rowBg = idx % 2 ? "#fafafa" : "#fff";
                  return (
                    <tr
                      key={r.id}
                      style={{ borderTop: `1px solid ${COLORS.border}`, background: rowBg, transition: "background .15s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
                    >
                      <Td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <FiUser /> {r.artist}
                        </div>
                      </Td>
                      <Td>{r.reportedBy}</Td>
                      <Td>{r.reason}</Td>
                      <Td>{formatDateTime(r.timestamp) || "—"}</Td>
                      <Td><StatusBadge status={r.adminStatus} /></Td>
                      <Td>
                        <button style={iconBtn} onClick={() => openModal(r)} title="View / Update">
                          <FiEye />
                        </button>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {viewer.open && (
          <div style={modalBackdrop}>
            <div style={modalCard}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontWeight: 900, color: COLORS.text }}>Report details</div>
                <button onClick={closeModal} style={closeBtn} aria-label="Close">✕</button>
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <Row label="Artist" value={viewer.artist} />
                <Row label="Reported by" value={viewer.reportedBy} />
                <Row label="Reason" value={viewer.reason} />
                <Row label="Reported at" value={formatDateTime(viewer.timestamp) || "—"} />
                <div>
                  <div style={lbl}>Status</div>
                  <select
                    value={viewer.adminStatus}
                    onChange={(e) => setViewer((v) => ({ ...v, adminStatus: e.target.value }))}
                    disabled={!canEdit || saving}
                    style={selectStyle}
                  >
                    <option value="open">Open</option>
                    <option value="hold">Hold</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div>
                  <div style={lbl}>
                    <FiMessageSquare /> Admin comment
                  </div>
                  <textarea
                    value={viewer.adminComment}
                    onChange={(e) => setViewer((v) => ({ ...v, adminComment: e.target.value }))}
                    placeholder='e.g., "Checked the report; holding the account and sent notice."'
                    rows={4}
                    disabled={!canEdit || saving}
                    style={taStyle}
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
                  <button onClick={closeModal} style={ghostBtn} disabled={saving}>
                    Close
                  </button>
                  <button
                    onClick={saveUpdate}
                    style={primaryBtn}
                    disabled={!canEdit || saving}
                    title={canEdit ? "Save update" : "No permission to update"}
                  >
                    <FiCheck /> {saving ? "Saving…" : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {toast && <div style={toastBox}>{toast}</div>}
      </div>
    </PermissionGuard>
  );
}

/* ───────── Parts ───────── */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td style={{ padding: 10, color: COLORS.text, verticalAlign: "top" }}>{children}</td>;
}

function Badge({ color, icon, children }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: `${color}1a`,
        color,
        fontWeight: 800,
        border: `1px solid ${color}33`,
      }}
    >
      {icon} {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = String(status || "open").toLowerCase();
  const map = {
    open: { bg: "#fff7ed", fg: COLORS.warn, label: "Open" },
    hold: { bg: "#f5f3ff", fg: COLORS.hold, label: "Hold" },
    resolved: { bg: "#ecfdf5", fg: COLORS.ok, label: "Resolved" },
  };
  const v = map[s] || map.open;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        fontWeight: 800,
        border: `1px solid ${v.fg}33`,
      }}
    >
      {v.label}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10 }}>
      <div style={{ color: COLORS.subtext, fontWeight: 700 }}>{label}</div>
      <div style={{ color: COLORS.text }}>{value}</div>
    </div>
  );
}

/* ───────── Helpers ───────── */
function toMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts === "number") return ts;
    if (ts?.toDate) return ts.toDate().getTime();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

function formatDateTime(v) {
  try {
    if (!v) return "";
    const d = typeof v === "string" ? new Date(v) : v.toDate ? v.toDate() : new Date(v);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch {
    return "";
  }
}

/* ───────── Styles ───────── */
const tdMuted = { padding: 12, color: COLORS.subtext };

const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: 12,
  marginBottom: 12,
};
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 280,
  flex: 1,
};
const searchInput = {
  border: "none",
  outline: "none",
  flex: 1,
  fontSize: 14,
};
const controlGroup = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: "#fff",
};
const select = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  paddingRight: 4,
  cursor: "pointer",
};

const iconBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const primaryBtn = {
  height: 38,
  padding: "0 14px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const ghostBtn = {
  height: 38,
  padding: "0 14px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  cursor: "pointer",
};

const selectStyle = {
  height: 36,
  padding: "0 10px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
};

const taStyle = {
  width: "100%",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  padding: 10,
  resize: "vertical",
  background: "#fff",
  minHeight: 90,
};

const lbl = {
  fontSize: 13,
  color: COLORS.text,
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontWeight: 800,
  marginBottom: 6,
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
  padding: 16,
};

const modalCard = {
  width: "100%",
  maxWidth: 680,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};

const closeBtn = {
  ...iconBtn,
  width: 32,
  height: 32,
  borderRadius: 8,
};

const toastBox = {
  position: "fixed",
  bottom: 16,
  right: 16,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
  color: COLORS.text,
  fontWeight: 800,
  zIndex: 80,
};
